// Flareyard motion core: one rAF loop, IntersectionObserver-gated canvases, reduced-motion aware.
// Measured systems (reference/motion-spec.md in the design project) are preserved: hero pitch 9px,
// dots 2-7px, cursor blob x~3, tau 0.32s, reach 170px; glyph pitch 6px; marquee 41px/s;
// glyph loops 2.73s / 2.57s.

export const RM = matchMedia("(prefers-reduced-motion: reduce)")
export const state = { reduced: RM.matches }
export const root = document.documentElement

/** Anything drawn by the loop: dot fields and the footer wordmark. */
export interface Frameable {
  dirty: boolean
  frame(dt: number): void
  onTheme?: (() => void) | undefined
}
export const fields: Frameable[] = []

RM.addEventListener("change", (e) => {
  state.reduced = e.matches
  root.classList.toggle("js", !e.matches)
  for (const f of fields) f.dirty = true
})

/** A 2D context, or a clear failure: every canvas here is ours, so a missing context is a bug. */
export const context2d = (
  canvas: HTMLCanvasElement,
  options?: CanvasRenderingContext2DSettings,
): CanvasRenderingContext2D => {
  const ctx = canvas.getContext("2d", options)
  if (!ctx) throw new Error("2D canvas context unavailable")
  return ctx
}

/* ---------- colour ramps from tokens ---------- */
const STEPS = [300, 400, 500, 600, 700, 800, 900]
/** Each family's ramp read from the design tokens, refreshed on theme change. */
export const ramps: Record<string, string[]> = {}
export function readRamps(): void {
  const cs = getComputedStyle(root)
  for (const key of Object.keys(ramps)) delete ramps[key]
  for (const fam of ["ember", "gray", "blue", "teal", "green", "amber"]) {
    ramps[fam] = STEPS.map((s) => cs.getPropertyValue(`--${fam}-${s}`).trim())
  }
  ramps.grayInk = [500, 600, 700, 800, 900, 1000, 1000].map((s) => cs.getPropertyValue(`--gray-${s}`).trim())
  for (const f of fields) {
    f.dirty = true
    f.onTheme?.()
  }
}
export const FAM: Readonly<Record<string, string>> = {
  compute: "ember",
  data: "blue",
  ai: "teal",
  messaging: "green",
  edge: "amber",
}

/* ---------- noise ---------- */
const P = new Uint8Array(512)
{
  let s = 1337
  const p = [...Array(256).keys()]
  for (let i = 255; i > 0; i--) {
    s = (s * 16807) % 2147483647
    const j = s % (i + 1)
    const a = p[i] ?? 0
    p[i] = p[j] ?? 0
    p[j] = a
  }
  for (let i = 0; i < 512; i++) P[i] = p[i & 255] ?? 0
}
const perm = (i: number): number => P[i] ?? 0
const fade = (t: number) => t * t * t * (t * (t * 6 - 15) + 10)
const lerp = (a: number, b: number, t: number) => a + (b - a) * t
function grad(h: number, x: number, y: number, z: number): number {
  const u = h < 8 ? x : y
  const v = h < 4 ? y : h === 12 || h === 14 ? x : z
  return (h & 1 ? -u : u) + (h & 2 ? -v : v)
}
export function noise3(x: number, y: number, z: number): number {
  const X = Math.floor(x) & 255
  const Y = Math.floor(y) & 255
  const Z = Math.floor(z) & 255
  x -= Math.floor(x)
  y -= Math.floor(y)
  z -= Math.floor(z)
  const u = fade(x)
  const v = fade(y)
  const w = fade(z)
  const A = perm(X) + Y
  const AA = perm(A) + Z
  const AB = perm(A + 1) + Z
  const B = perm(X + 1) + Y
  const BA = perm(B) + Z
  const BB = perm(B + 1) + Z
  return lerp(
    lerp(
      lerp(grad(perm(AA) & 15, x, y, z), grad(perm(BA) & 15, x - 1, y, z), u),
      lerp(grad(perm(AB) & 15, x, y - 1, z), grad(perm(BB) & 15, x - 1, y - 1, z), u),
      v,
    ),
    lerp(
      lerp(grad(perm(AA + 1) & 15, x, y, z - 1), grad(perm(BA + 1) & 15, x - 1, y, z - 1), u),
      lerp(grad(perm(AB + 1) & 15, x, y - 1, z - 1), grad(perm(BB + 1) & 15, x - 1, y - 1, z - 1), u),
      v,
    ),
    w,
  )
}
export const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)
export const smooth = (a: number, b: number, x: number): number => {
  const t = clamp01((x - a) / (b - a))
  return t * t * (3 - 2 * t)
}
export const easeOut = (t: number): number => 1 - (1 - clamp01(t)) ** 3
const hash2 = (i: number, j: number) => {
  const s = Math.sin(i * 127.1 + j * 311.7) * 43758.5453
  return s - Math.floor(s)
}

/* ---------- the one loop ---------- */
type Task = (dt: number, now: number) => void
const tasks = new Set<Task>()
export const onFrame = (fn: Task): (() => boolean) => {
  tasks.add(fn)
  return () => tasks.delete(fn)
}
let last = performance.now()
function loop(): void {
  const now = performance.now()
  const dt = Math.min(0.1, (now - last) / 1000)
  last = now
  for (const fn of tasks) fn(dt, now)
  for (const f of fields) f.frame(dt)
  requestAnimationFrame(loop)
}
/**
 * Bumped whenever the page scrolls, the window resizes or the page's own size changes (late fonts,
 * images, reflow). Scroll-driven handlers read layout, so they skip frames where nothing moved instead
 * of forcing a layout pass 60 times a second.
 */
let viewportVersion = 0
const bumpViewport = () => {
  viewportVersion++
}
addEventListener("scroll", bumpViewport, { passive: true })
addEventListener("resize", bumpViewport)
new ResizeObserver(bumpViewport).observe(document.documentElement)

/** A per-handler check: true on the first call and whenever the viewport changed since the last call. */
export const viewportWatcher = (): (() => boolean) => {
  let seen = -1
  return () => {
    if (seen === viewportVersion) return false
    seen = viewportVersion
    return true
  }
}

export function start(): void {
  readRamps()
  requestAnimationFrame(loop)
  matchMedia("(prefers-color-scheme: dark)").addEventListener("change", readRamps)
}

/* ---------- DotField ---------- */
export type SampleFn = (this: DotField, x: number, y: number, t: number, i: number, j: number) => number
export interface Hole {
  readonly l: number
  readonly r: number
  readonly t: number
  readonly b: number
}
export interface DotFieldOptions {
  sample: SampleFn
  pitch?: number
  min?: number
  max?: number
  family?: string
  floor?: number
  pointer?: boolean
  levels?: number
  boost?: number
  bloom?: number
  intro?: number
  gain?: number
  ripple?: boolean
  lo?: number
  hi?: number
  pointerHost?: Element | null
  onResize?: (this: DotField) => void
  onTheme?: (this: DotField) => void
}

export class DotField implements Frameable {
  readonly c: HTMLCanvasElement
  readonly ctx: CanvasRenderingContext2D
  sample: SampleFn
  pitch: number
  min: number
  max: number
  family: string
  floor: number
  pointer: boolean
  levels: number
  boost: number
  bloom: number
  intro: number
  gain: number
  ripple: boolean
  lo: number | undefined
  hi: number | undefined
  pointerHost: Element | null | undefined
  onResize: ((this: DotField) => void) | undefined
  onTheme: (() => void) | undefined

  time = 0
  visible = false
  dirty = true
  hover = 0
  hoverTarget = 0
  px = -1e4
  py = -1e4
  inside = 0
  w = 0
  h = 0
  cols = 0
  rows = 0
  heat = new Float32Array(0)
  /** Areas the hero keeps clear of dots, so text over the field stays readable. */
  holes: Hole[] | undefined
  /** Per-cell luminance of an illustration being dithered. */
  lum: Float32Array | undefined
  /** Orbit glyph phase and the time it was last advanced. */
  ph: number | undefined
  lt: number | undefined

  constructor(canvas: HTMLCanvasElement, o: DotFieldOptions) {
    this.sample = o.sample
    this.pitch = o.pitch ?? 9
    this.min = o.min ?? 2
    this.max = o.max ?? 7
    this.family = o.family ?? "ember"
    this.floor = o.floor ?? 0.07
    this.pointer = o.pointer ?? false
    this.levels = o.levels ?? 6
    this.boost = o.boost ?? 0
    this.bloom = o.bloom ?? 0
    this.intro = o.intro ?? 1
    this.gain = o.gain ?? 1
    this.ripple = o.ripple ?? false
    this.lo = o.lo
    this.hi = o.hi
    this.pointerHost = o.pointerHost
    this.onResize = o.onResize
    const onTheme = o.onTheme
    this.onTheme = onTheme ? () => onTheme.call(this) : undefined
    this.c = canvas
    this.ctx = context2d(canvas)
    new ResizeObserver(() => this.resize()).observe(canvas)
    new IntersectionObserver(
      (entries) => {
        this.visible = entries[0]?.isIntersecting ?? false
        if (this.visible) this.dirty = true
      },
      { rootMargin: "120px" },
    ).observe(canvas)
    if (this.pointer || this.ripple) {
      const host = this.pointerHost || canvas.parentElement
      host?.addEventListener("pointermove", (e) => {
        const r = canvas.getBoundingClientRect()
        this.px = (e as PointerEvent).clientX - r.left
        this.py = (e as PointerEvent).clientY - r.top
        this.inside = 1
      })
      host?.addEventListener("pointerleave", () => {
        this.px = this.py = -1e4
        this.inside = 0
      })
    }
    fields.push(this)
    this.resize()
  }

  resize(): void {
    const r = this.c.getBoundingClientRect()
    // A canvas measured before layout settles can be 0 tall; the ResizeObserver calls again once it isn't.
    if (!r.width || !r.height) return
    const d = Math.min(devicePixelRatio || 1, 2)
    this.w = r.width
    this.h = r.height
    this.c.width = Math.round(r.width * d)
    this.c.height = Math.round(r.height * d)
    this.ctx.setTransform(d, 0, 0, d, 0, 0)
    this.cols = Math.ceil(this.w / this.pitch)
    this.rows = Math.ceil(this.h / this.pitch)
    this.heat = new Float32Array(this.cols * this.rows)
    this.onResize?.call(this)
    this.dirty = true
  }

  /**
   * Redraw every `stride` frames, adapted to what a redraw costs on this device. Desktops stay at every
   * frame; a slow phone CPU drops to every 2nd or 4th, with the skipped time carried into the next redraw
   * so the motion keeps its speed. Without this the loop never left the phone's main thread idle.
   */
  stride = 1
  skipped = 0
  carry = 0
  cost = 0

  frame(dt: number): void {
    if (!this.visible || !this.w) return
    const red = state.reduced
    if (red && !this.dirty) return
    if (!red && !this.dirty) {
      this.carry += dt
      if (++this.skipped < this.stride) return
      dt = this.carry
    }
    this.skipped = 0
    this.carry = 0
    const started = performance.now()
    this.draw(dt)
    const ms = performance.now() - started
    this.cost = this.cost ? this.cost * 0.85 + ms * 0.15 : ms
    this.stride = this.cost > 14 ? 4 : this.cost > 7 ? 2 : 1
  }

  private draw(dt: number): void {
    const red = state.reduced
    this.hover += (this.hoverTarget - this.hover) * (red ? 1 : 1 - Math.exp(-dt / 0.12))
    this.time += red ? 0 : dt * (1 + this.hover * this.boost)
    const t = this.time
    const { ctx, pitch, cols, rows, levels, heat } = this
    const max = this.max + this.hover * this.bloom
    const ramp = (ramps[this.family] ?? ramps.ember ?? []).slice(this.lo ?? 0, (this.hi ?? 6) + 1)
    ctx.clearRect(0, 0, this.w, this.h)
    const buckets: number[][] = Array.from({ length: levels }, () => [])
    const k = red ? 0 : 1 - Math.exp(-dt / 0.32)
    const R = 170
    const intro = this.intro
    const gain = this.gain * (1 + this.hover * this.bloom * 0.08)
    for (let j = 0; j < rows; j++) {
      const cy = j * pitch + pitch / 2
      for (let i = 0; i < cols; i++) {
        if (intro < 1 && hash2(i, j) > intro * 1.15) continue
        const cx = i * pitch + pitch / 2
        let v = this.sample(cx, cy, t, i, j) * gain
        if (this.pointer) {
          const id = j * cols + i
          const dd = Math.hypot(cx - this.px, cy - this.py)
          const was = heat[id] ?? 0
          heat[id] = was + ((dd < R ? 1 - smooth(0, R, dd) : 0) - was) * k
          // Read back through the Float32Array, as the prototype did, so rounding matches it exactly.
          const h = heat[id] ?? 0
          if (h > 0.002) v = v * (1 + 2 * h) + 0.03 * h
        }
        if (this.ripple && this.inside) {
          const dd = Math.hypot(cx - this.px, cy - this.py)
          if (dd < 220) v *= 1 + (1 - dd / 220) * (0.45 + 0.45 * Math.sin(dd / 11 - t * 7))
        }
        if (v < this.floor) continue
        buckets[Math.min(levels - 1, Math.floor(clamp01(v) * levels))]?.push(cx, cy)
      }
    }
    for (let L = 0; L < levels; L++) {
      const b = buckets[L]
      if (!b?.length) continue
      const s = Math.round(this.min + ((max - this.min) * (L + 0.5)) / levels)
      ctx.fillStyle =
        ramp[Math.min(ramp.length - 1, Math.round((L * (ramp.length - 1)) / (levels - 1)))] ?? ""
      ctx.beginPath()
      for (let q = 0; q < b.length; q += 2) {
        ctx.rect(Math.round((b[q] ?? 0) - s / 2), Math.round((b[q + 1] ?? 0) - s / 2), s, s)
      }
      ctx.fill()
    }
    this.dirty = false
  }
}

/* ---------- hero ribbon (measured) ---------- */
/**
 * Everything in the ribbon that depends only on the row and the time: its centre line, the three
 * strands' offsets and widths, and the vertical envelope. The field walks row by row, so computing these
 * once per row instead of once per dot removes nine sines per dot, with bit-identical results.
 */
const heroRow = {
  field: null as DotField | null,
  y: Number.NaN,
  t: Number.NaN,
  h: 0,
  cxl: 0,
  env: 0,
  off: new Float64Array(3),
  width: new Float64Array(3),
}

export const heroSample: SampleFn = function (x, y, t) {
  const W = this.w
  const H = this.h
  const row = heroRow
  if (row.field !== this || row.y !== y || row.t !== t || row.h !== H) {
    const v = y / H
    row.field = this
    row.y = y
    row.t = t
    row.h = H
    row.cxl = 0.8 - 0.4 * v - 0.05 * Math.sin(v * 3.1 + 0.4)
    for (let s = 0; s < 3; s++) {
      const ph = s * 1.9
      row.off[s] =
        0.22 * Math.sin(v * 4.0 + ph + t * 0.16) + 0.09 * Math.sin(v * 8.5 - ph + t * 0.25) - 0.2 + s * 0.2
      row.width[s] = 0.045 + 0.04 * (0.5 + 0.5 * Math.sin(v * 5.0 + s + t * 0.08))
    }
    row.env = smooth(0, 0.3, 1.05 - v) * smooth(0, 0.15, v + 0.1)
  }
  const u = x / W
  const across = ((u - row.cxl) * W) / 640
  let I = 0
  for (let s = 0; s < 3; s++) {
    const d = (across - (row.off[s] ?? 0)) / (row.width[s] ?? 1)
    I += Math.exp(-d * d) * (s === 1 ? 1 : 0.55)
  }
  const haze = Math.exp(-((across / 0.42) ** 2)) * 0.2
  const env = row.env
  // Far from the ribbon the value can't become a dot whatever the noise does: the noise terms multiply
  // by at most 1.74 and the pointer at most triples a value and adds 0.03, against the hero's 0.07 floor
  // at gain 1. Skipping the two noise calls there halves the field's cost with identical output.
  const base = (I * 0.78 + haze) * env
  if (base < 0.007) return 0
  const n = noise3(x / 150, y / 150, t * 1.25) * 0.5 + 0.5
  const n2 = noise3(x / 70 + 7, y / 70, t * 1.2) * 0.5 + 0.5
  let val = base * (0.2 + 1.3 * n * n) * (0.68 + 0.48 * n2)
  if (this.holes) {
    for (const r of this.holes) {
      const dx = Math.max(r.l - x, 0, x - r.r)
      const dy = Math.max(r.t - y, 0, y - r.b)
      val *= smooth(0, 36, Math.hypot(dx, dy)) * 0.85 + 0.15
    }
  }
  return val
}

/* ---------- card glyphs (6px pitch, measured periods) ---------- */
const rnd = (n: number) => {
  const s = Math.sin(n * 127.1) * 43758.5453
  return s - Math.floor(s)
}
export const glyphs = {
  ring: function (x, y, t) {
    const cx = this.w / 2
    const cy = this.h / 2
    const r0 = Math.min(this.w, this.h) * 0.34
    const a = Math.atan2(y - cy, x - cx)
    const d = Math.hypot(x - cx, y - cy)
    const ex = 2 + (0.5 + 0.5 * Math.sin(t * 0.8)) * 3 + this.hover * 1.5
    const ca = Math.abs(Math.cos(a))
    const sa = Math.abs(Math.sin(a))
    const rs = 1 / (ca ** ex + sa ** ex) ** (1 / ex)
    const R =
      r0 *
      rs *
      (1 +
        0.07 * noise3(Math.cos(a) * 1.4, Math.sin(a) * 1.4, t * 0.9) +
        this.hover * 0.04 * Math.sin(a * 6 - t * 3)) *
      (1 + this.hover * 0.08)
    const ring = Math.exp(-(((d - R) / (7 + this.hover * 3)) ** 2))
    const inner = d < R ? 0.1 + this.hover * 0.22 * (0.5 + 0.5 * Math.sin(d / 9 - t * 4)) : 0
    return Math.max(ring * 1.05, inner, 0.08 * (0.5 + 0.5 * noise3(x / 40, y / 40, t * 0.4)))
  },
  dash: function (_x, y, t, i, j) {
    if (j % 2) return 0.05 * rnd(i * 3.1 + j)
    const row = j >> 1
    const speed = rnd(row) < 0.22 ? 0 : 29 + 19 * rnd(row + 9)
    const c = i - Math.floor((t * speed * (1 + this.hover * 1.4)) / this.pitch)
    const seg = Math.floor(c / 7)
    const pos = ((c % 7) + 7) % 7
    const len = 2 + Math.floor(rnd(seg * 7.3 + row * 1.9) * 5)
    if (!(pos < len && rnd(seg * 3.7 + row * 5.1) > 0.35)) return 0.05 * rnd(i * 1.3 + j * 7.7)
    return (
      (pos === len - 1 ? 1 : 0.42 + 0.2 * rnd(seg + row)) *
      (1 - this.hover * 0.55 * Math.abs(y / this.h - 0.5))
    )
  },
  orbit: function (x, y, t) {
    const cx = this.w / 2
    const cy = this.h / 2
    const R = Math.min(this.w, this.h) * 0.25
    const per = this.hover > 0.5 ? 2.73 : 4.1
    this.ph = (this.ph || 0) + (t - (this.lt ?? t)) * ((2 * Math.PI) / per)
    this.lt = t
    const ph = this.ph
    const dx = (x - cx) / R
    const dy = (y - cy) / R
    const rr = dx * dx + dy * dy
    let v = 0
    if (rr < 1) {
      const z = Math.sqrt(1 - rr)
      const lon = Math.atan2(dx, z) + ph
      const lat = Math.asin(clamp01((dy + 1) / 2) * 2 - 1)
      v =
        (noise3(Math.cos(lon) * 1.6 + 3, Math.sin(lon) * 1.6, lat * 1.8) > 0.02 ? 1 : 0.35) *
        (0.35 + 0.65 * clamp01(-dx * 0.5 - dy * 0.4 + z * 0.8))
    }
    const rd = Math.abs(Math.hypot((x - cx) / (R * 2.1), (y - cy) / (R * 0.55)) - 1)
    if (!(rr < 1 && y - cy < 0)) v = Math.max(v, Math.exp(-rd * rd * 120) * 0.42)
    const sd = Math.hypot(x - (cx + Math.cos(ph) * R * 2.1), y - (cy + Math.sin(ph) * R * 0.55))
    if (!(rr < 1 && Math.sin(ph) < 0)) v = Math.max(v, Math.exp((-sd * sd) / 90))
    return v
  },
  wave: function (x, y, t) {
    const w = Math.PI / 2.57
    const k = 0.085
    const sy = this.h / 2
    let a =
      Math.sin(Math.hypot(x - this.w * 0.3, y - sy) * k - t * w) +
      Math.sin(Math.hypot(x - this.w * 0.7, y - sy) * k - t * w)
    let n = 2
    if (this.hover > 0.01) {
      a += this.hover * Math.sin(Math.hypot(x - this.w / 2, y - sy) * k * 1.4 - t * w * 1.5)
      n += this.hover
    }
    return (
      (Math.abs(a) / n) ** 2.2 *
      smooth(0, 0.22, 1 - Math.abs(x / this.w - 0.5) * 1.6) *
      smooth(0, 0.3, 1 - Math.abs(y / this.h - 0.5) * 1.7) *
      1.1
    )
  },
} satisfies Record<string, SampleFn>
export type GlyphName = keyof typeof glyphs
export const GLYPH_BOOST: Readonly<Record<GlyphName, number>> = { ring: 1.0, dash: 0, orbit: 0, wave: 0.8 }
const isGlyph = (name: string | undefined): name is GlyphName => name !== undefined && name in glyphs

export function mountGlyph(canvas: HTMLCanvasElement): DotField | undefined {
  const name = canvas.dataset.glyph
  if (!isGlyph(name)) return undefined
  const g0 = glyphs[name]
  const f = new DotField(canvas, {
    pitch: 6,
    min: 1,
    max: 5,
    floor: 0.06,
    boost: GLYPH_BOOST[name],
    bloom: 1.5,
    family: canvas.dataset.family || "ember",
    sample(x, y, t, i, j) {
      return x < 60 && y < 52 ? 0 : g0.call(this, x, y, t, i, j)
    },
  })
  const host = canvas.closest("[data-hover-host]") || canvas.parentElement
  for (const e of ["pointerenter", "focusin"]) host?.addEventListener(e, () => (f.hoverTarget = 1))
  for (const e of ["pointerleave", "focusout"]) host?.addEventListener(e, () => (f.hoverTarget = 0))
  return f
}

/* ---------- app illustrations (drawn in the family colour, then dithered) ---------- */
type Point = readonly [number, number]
export function drawArt(
  kind: string | undefined,
  fam: string,
  w: number,
  h: number,
  seed = 0,
): HTMLCanvasElement {
  const cs = getComputedStyle(root)
  const V = (s: number) => cs.getPropertyValue(`--${fam}-${s}`).trim()
  const c = document.createElement("canvas")
  c.width = w
  c.height = h
  const g = context2d(c)
  const m = Math.min(w, h)
  g.fillStyle = V(200)
  g.fillRect(0, 0, w, h)
  g.lineJoin = "round"
  g.lineCap = "round"
  const box = (x: number, y: number, bw: number, bh: number, s: number) => {
    g.fillStyle = V(s)
    g.fillRect(x, y, bw, bh)
  }
  switch (kind) {
    case "email":
      for (let k = 4; k >= 0; k--) {
        const ox = w * 0.5 + k * m * 0.05 - m * 0.3
        const oy = h * 0.5 - k * m * 0.06 - m * 0.12
        const ew = m * 0.62
        const eh = m * 0.4
        box(ox, oy, ew, eh, k ? 300 + Math.min(k, 3) * 100 : 100)
        g.strokeStyle = V(800)
        g.lineWidth = m * 0.012
        g.strokeRect(ox, oy, ew, eh)
        if (!k) {
          g.beginPath()
          g.moveTo(ox, oy)
          g.lineTo(ox + ew / 2, oy + eh * 0.55)
          g.lineTo(ox + ew, oy)
          g.stroke()
          box(ox + ew * 0.72, oy + eh * 0.68, ew * 0.18, eh * 0.18, 700)
        }
      }
      break
    case "scheduler": {
      const cols = 5
      const rows = 6
      const gx = w * 0.12
      const gy = h * 0.16
      const cw = (w * 0.76) / cols
      const ch = (h * 0.72) / rows
      for (let r = 0; r < rows; r++) {
        for (let q = 0; q < cols; q++) {
          box(
            gx + q * cw + 4,
            gy + r * ch + 4,
            cw - 8,
            ch - 8,
            (r * 7 + q * 3 + seed) % 5 === 0 ? 700 : (r + q) % 3 ? 300 : 400,
          )
        }
      }
      box(gx, gy - h * 0.07, w * 0.76, h * 0.03, 900)
      break
    }
    case "agent": {
      const pts: Point[] = [
        [0.3, 0.25],
        [0.7, 0.2],
        [0.5, 0.5],
        [0.25, 0.72],
        [0.72, 0.75],
        [0.5, 0.88],
        [0.85, 0.48],
        [0.15, 0.48],
      ]
      g.strokeStyle = V(600)
      g.lineWidth = m * 0.02
      const edges: Point[] = [
        [0, 2],
        [1, 2],
        [2, 3],
        [2, 4],
        [3, 5],
        [4, 5],
        [1, 6],
        [0, 7],
        [6, 4],
        [7, 3],
      ]
      for (const [a, b] of edges) {
        const pa = pts[a]
        const pb = pts[b]
        if (!pa || !pb) continue
        g.beginPath()
        g.moveTo(pa[0] * w, pa[1] * h)
        g.lineTo(pb[0] * w, pb[1] * h)
        g.stroke()
      }
      pts.forEach((p, i) => {
        g.fillStyle = i === 2 ? V(800) : V(i % 2 ? 500 : 700)
        g.beginPath()
        g.arc(p[0] * w, p[1] * h, m * (i === 2 ? 0.11 : 0.05), 0, 7)
        g.fill()
      })
      break
    }
    case "meetings": {
      const n = 46
      const bw = (w * 0.8) / n
      for (let i = 0; i < n; i++) {
        const a = Math.abs(Math.sin(i * 0.55 + seed) * Math.cos(i * 0.21)) * 0.6 + 0.08
        box(w * 0.1 + i * bw + 1, h * 0.42 - a * h * 0.3, bw - 3, a * h * 0.6, i > 18 && i < 30 ? 800 : 600)
      }
      for (let q = 0; q < 3; q++) box(w * (0.1 + q * 0.28), h * 0.74, w * 0.24, h * 0.14, q === 1 ? 700 : 400)
      break
    }
    case "forms":
      for (let r = 0; r < 4; r++) {
        box(w * 0.2, h * (0.16 + r * 0.17), w * 0.3, h * 0.03, 600)
        box(w * 0.2, h * (0.21 + r * 0.17), w * 0.6, h * 0.07, r === 1 ? 400 : 300)
      }
      box(w * 0.2, h * 0.84, w * 0.22, h * 0.08, 800)
      break
    case "analytics":
      for (let i = 0; i < 12; i++) {
        const a = 0.2 + 0.7 * Math.abs(Math.sin(i * 0.7 + seed))
        box(w * (0.1 + i * 0.067), h * (0.85 - a * 0.6), w * 0.045, h * a * 0.6, i === 8 ? 800 : 500)
      }
      g.strokeStyle = V(900)
      g.lineWidth = m * 0.012
      g.beginPath()
      for (let i = 0; i < 12; i++) {
        const x = w * (0.122 + i * 0.067)
        const y = h * (0.5 - 0.25 * Math.sin(i * 0.5 + seed))
        if (i) g.lineTo(x, y)
        else g.moveTo(x, y)
      }
      g.stroke()
      break
    case "files":
      for (let k = 0; k < 4; k++) {
        const x = w * (0.18 + k * 0.14)
        const y = h * (0.18 + k * 0.06)
        box(x, y, w * 0.34, h * 0.52, 300 + k * 100)
        g.strokeStyle = V(800)
        g.lineWidth = m * 0.01
        g.strokeRect(x, y, w * 0.34, h * 0.52)
      }
      box(w * 0.62, h * 0.62, w * 0.16, h * 0.16, 800)
      break
    case "search":
      g.strokeStyle = V(800)
      g.lineWidth = m * 0.05
      g.beginPath()
      g.arc(w * 0.44, h * 0.44, m * 0.2, 0, 7)
      g.stroke()
      g.beginPath()
      g.moveTo(w * 0.44 + m * 0.15, h * 0.44 + m * 0.15)
      g.lineTo(w * 0.44 + m * 0.32, h * 0.44 + m * 0.32)
      g.stroke()
      for (let r = 0; r < 3; r++) box(w * 0.34, h * (0.38 + r * 0.05), w * (0.2 - r * 0.04), h * 0.025, 500)
      break
    case "inbox":
      for (let r = 0; r < 5; r++) {
        const right = r % 2
        box(
          right ? w * 0.38 : w * 0.14,
          h * (0.12 + r * 0.16),
          w * 0.48,
          h * 0.11,
          right ? 700 : 300 + r * 60,
        )
      }
      break
    case "gateway":
      box(w * 0.36, h * 0.1, w * 0.28, h * 0.8, 800)
      box(w * 0.39, h * 0.16, w * 0.22, h * 0.64, 200)
      for (let r = 0; r < 4; r++) {
        box(r % 2 ? w * 0.46 : w * 0.41, h * (0.2 + r * 0.13), w * 0.13, h * 0.08, r % 2 ? 600 : 400)
      }
      break
    case "links":
      g.strokeStyle = V(800)
      g.lineWidth = m * 0.06
      for (const [x, y] of [
        [0.36, 0.56],
        [0.6, 0.44],
      ] as const) {
        g.beginPath()
        g.roundRect(w * x - m * 0.2, h * y - m * 0.09, m * 0.4, m * 0.18, m * 0.09)
        g.stroke()
      }
      break
    case "status":
      for (let i = 0; i < 30; i++)
        box(w * (0.1 + i * 0.027), h * 0.3, w * 0.02, h * 0.4, i === 21 ? 800 : i === 22 ? 600 : 500)
      box(w * 0.1, h * 0.78, w * 0.4, h * 0.05, 700)
      break
    default:
      box(w * 0.3, h * 0.3, w * 0.4, h * 0.4, 600)
  }
  return c
}

export function lumaGrid(
  src: CanvasImageSource,
  cols: number,
  rows: number,
  invert: boolean,
  norm = true,
): Float32Array {
  // getImageData throws on an empty canvas; an empty grid has nothing to sample anyway.
  if (cols < 1 || rows < 1) return new Float32Array(0)
  const c = document.createElement("canvas")
  c.width = cols
  c.height = rows
  const g = context2d(c, { willReadFrequently: true })
  g.drawImage(src, 0, 0, cols, rows)
  const d = g.getImageData(0, 0, cols, rows).data
  const out = new Float32Array(cols * rows)
  for (let i = 0; i < out.length; i++) {
    const L = (0.2126 * (d[i * 4] ?? 0) + 0.7152 * (d[i * 4 + 1] ?? 0) + 0.0722 * (d[i * 4 + 2] ?? 0)) / 255
    out[i] = invert ? 1 - L : L
  }
  if (!norm) return out
  let lo = 1
  let hi = 0
  for (const v of out) {
    if (v < lo) lo = v
    if (v > hi) hi = v
  }
  const span = Math.max(0.05, hi - lo)
  for (let i = 0; i < out.length; i++) out[i] = (((out[i] ?? 0) - lo) / span) ** 0.75
  return out
}

/** Mounts one `.art[data-art]` (app cards, detail hero, screens): dither, ripple, dither-to-image resolve. */
export function mountArt(el: HTMLElement): void {
  if (el.dataset.mounted) return
  el.dataset.mounted = "1"
  const kind = el.dataset.art
  const famId = el.dataset.family
  const fam = (famId && FAM[famId]) || famId || "ember"
  const seed = Number(el.dataset.seed || 0)
  const cv = document.createElement("canvas")
  cv.setAttribute("aria-hidden", "true")
  el.prepend(cv)
  // The yard is a photo-like illustration; every other kind is drawn on a canvas in the family colour.
  const photo = kind === "yard" ? document.createElement("img") : null
  const drawn = photo ? null : document.createElement("canvas")
  if (photo) {
    photo.src = "/assets/img/yard.webp"
    photo.alt = ""
    photo.width = 640
    photo.height = 378
  }
  const img = photo ?? drawn
  if (!img) return
  img.className = "img"
  img.setAttribute("aria-hidden", "true")
  el.appendChild(img)
  new DotField(cv, {
    pitch: photo ? 7 : 8,
    min: 2,
    max: photo ? 6 : 7,
    family: fam,
    floor: 0.08,
    ripple: !photo,
    pointerHost: el.closest("a,.art") || el,
    onResize() {
      const W = Math.round(this.w)
      const H = Math.round(this.h)
      let source: CanvasImageSource
      if (photo) {
        if (!photo.complete) {
          photo.onload = () => this.resize()
          return
        }
        source = photo
      } else {
        const art = drawArt(kind, fam, W, H, seed)
        if (drawn) {
          drawn.width = W
          drawn.height = H
          context2d(drawn).drawImage(art, 0, 0)
        }
        source = art
      }
      // Dots go where the drawing is: on a light page dark marks are ink, on a dark page light marks are.
      const bg = getComputedStyle(root).getPropertyValue("--background-100").trim()
      const dark = Number.parseInt(bg.slice(1, 3), 16) < 128
      const lum = lumaGrid(source, this.cols, this.rows, photo ? true : !dark)
      if (photo) for (let i = 0; i < lum.length; i++) lum[i] = clamp01((lum[i] ?? 0) * 2.6)
      this.lum = lum
    },
    onTheme() {
      if (!photo) this.resize()
    },
    sample(_x, _y, t, i, j) {
      return this.lum
        ? (this.lum[j * this.cols + i] ?? 0) * (0.86 + 0.18 * noise3(i / 9, j / 9, t * 0.35))
        : 0
    },
  })
  const host = el.closest("a") || el
  host.addEventListener("pointermove", (e) => {
    const r = el.getBoundingClientRect()
    el.style.setProperty("--mx", `${e.clientX - r.left}px`)
    el.style.setProperty("--my", `${e.clientY - r.top}px`)
  })
}

export function lazyArt(scope: ParentNode = document): void {
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting && e.target instanceof HTMLElement) {
          mountArt(e.target)
          io.unobserve(e.target)
        }
      }
    },
    { rootMargin: "300px" },
  )
  for (const el of scope.querySelectorAll(".art[data-art]")) io.observe(el)
}

/* ---------- reveals, magnetic buttons, counters ---------- */
export function reveals(scope: ParentNode = document): void {
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add("in")
          io.unobserve(e.target)
        }
      }
    },
    { rootMargin: "0px 0px -10% 0px" },
  )
  for (const el of scope.querySelectorAll(".rv,.rh")) io.observe(el)
}

export function magnetic(scope: ParentNode = document): void {
  for (const b of scope.querySelectorAll<HTMLElement>(".mag")) {
    b.addEventListener("pointermove", (e) => {
      if (state.reduced) return
      const r = b.getBoundingClientRect()
      b.style.setProperty("--mx", `${((e.clientX - r.left) / r.width - 0.5) * 10}px`)
      b.style.setProperty("--my", `${((e.clientY - r.top) / r.height - 0.5) * 8}px`)
    })
    b.addEventListener("pointerleave", () => {
      b.style.setProperty("--mx", "0px")
      b.style.setProperty("--my", "0px")
    })
  }
}

// Counters: each digit sits in its own fixed-width cell, so nothing jitters while ticking.
const cells = (str: string) =>
  [...str].map((ch) => (/\d/.test(ch) ? `<span class="dg">${ch}</span>` : ch)).join("")
interface Counter {
  readonly el: HTMLElement
  readonly n: Element
  readonly to: number
  t: number
}
export function counters(scope: ParentNode = document): void {
  const list: Counter[] = []
  for (const el of scope.querySelectorAll<HTMLElement>("[data-count]")) {
    const to = Number(el.dataset.count)
    const n = el.querySelector(".n")
    if (!n) continue
    n.classList.add("tab")
    n.innerHTML = cells((state.reduced ? to : 0).toLocaleString("en-US"))
    if (!state.reduced) list.push({ el, n, to, t: -1 })
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        const c = list.find((c) => c.el === e.target)
        if (e.isIntersecting && c && c.t < 0) c.t = 0
      }
    },
    { threshold: 0.6 },
  )
  for (const c of list) io.observe(c.el)
  onFrame((dt) => {
    for (const c of list) {
      if (c.t < 0 || c.t > 1.4) continue
      c.t += dt
      c.n.innerHTML = cells(Math.round(c.to * easeOut(c.t / 1.2)).toLocaleString("en-US"))
    }
  })
}

/* ---------- footer: big dotted "flareyard" wordmark, "flare" in the gray ramp, "yard" in ember,
   fitted exactly to the container's width from measured ink bounds, with cursor scatter-and-settle ---------- */
const WM_FONT = (px: number) => `700 ${px}px "Funnel Display", sans-serif`
const WM_TRACK = -0.03
function measureWord(ctx: CanvasRenderingContext2D, px: number) {
  ctx.font = WM_FONT(px)
  ctx.letterSpacing = `${WM_TRACK * px}px`
  const all = ctx.measureText("flareyard")
  const fl = ctx.measureText("flare")
  return {
    left: all.actualBoundingBoxLeft,
    right: all.actualBoundingBoxRight,
    asc: all.actualBoundingBoxAscent,
    desc: all.actualBoundingBoxDescent,
    split: fl.width,
  }
}

export class Giant implements Frameable {
  readonly c: HTMLCanvasElement
  readonly ctx: CanvasRenderingContext2D
  time = 0
  visible = false
  dirty = true
  px = -1e4
  py = -1e4
  w = 0
  h = 0
  pitch = 9
  cols = 0
  splitX = 0
  mask: Float32Array = new Float32Array(0)
  cells: number[] | undefined
  ox = new Float32Array(0)
  oy = new Float32Array(0)
  vx = new Float32Array(0)
  vy = new Float32Array(0)

  constructor(canvas: HTMLCanvasElement) {
    this.c = canvas
    this.ctx = context2d(canvas)
    const host = canvas.parentElement
    host?.addEventListener("pointermove", (e) => {
      const r = canvas.getBoundingClientRect()
      this.px = e.clientX - r.left
      this.py = e.clientY - r.top
    })
    host?.addEventListener("pointerleave", () => {
      this.px = this.py = -1e4
    })
    if (host) new ResizeObserver(() => this.resize()).observe(host)
    new IntersectionObserver(
      (entries) => {
        this.visible = entries[0]?.isIntersecting ?? false
        this.dirty = true
      },
      { rootMargin: "160px" },
    ).observe(canvas)
    fields.push(this)
  }

  resize(): void {
    const host = this.c.parentElement
    if (!host) return
    const W = Math.floor(host.getBoundingClientRect().width)
    if (!W) return
    this.pitch = W < 600 ? 5 : W < 1100 ? 7 : 9
    const probe = context2d(document.createElement("canvas"))
    const m0 = measureWord(probe, 100)
    const ink0 = m0.left + m0.right // ink width at 100px, tracking included
    const px = (100 * (W - 1)) / ink0 // exact fit: first ink pixel at 0, last at W-1
    const mm = measureWord(probe, px)
    const H = Math.ceil(mm.asc + mm.desc + this.pitch)
    host.style.height = `${H}px`
    const d = Math.min(devicePixelRatio || 1, 2)
    this.w = W
    this.h = H
    this.c.width = Math.round(W * d)
    this.c.height = Math.round(H * d)
    this.ctx.setTransform(d, 0, 0, d, 0, 0)
    const cols = Math.ceil(W / this.pitch)
    const rows = Math.ceil(H / this.pitch)
    const S = 3
    const m = document.createElement("canvas")
    m.width = cols * S
    m.height = rows * S
    const g = context2d(m)
    const sc = m.width / W
    const x0 = mm.left
    const base = mm.asc + this.pitch / 2
    g.fillStyle = "white"
    g.fillRect(0, 0, m.width, m.height)
    g.fillStyle = "black"
    g.textBaseline = "alphabetic"
    g.font = WM_FONT(px * sc)
    g.letterSpacing = `${WM_TRACK * px * sc}px`
    g.fillText("flareyard", x0 * sc, base * sc)
    this.mask = lumaGrid(m, cols, rows, true, false)
    this.splitX = x0 + mm.split // cells right of this belong to "yard"
    this.cols = cols
    const cellIds: number[] = []
    for (let j = 0; j < rows; j++) {
      for (let i = 0; i < cols; i++) if ((this.mask[j * cols + i] ?? 0) > 0.22) cellIds.push(j * cols + i)
    }
    this.cells = cellIds
    const n = cellIds.length
    this.ox = new Float32Array(n)
    this.oy = new Float32Array(n)
    this.vx = new Float32Array(n)
    this.vy = new Float32Array(n)
    this.dirty = true
  }

  frame(dt: number): void {
    const cellIds = this.cells
    if (!this.visible || !cellIds) return
    const red = state.reduced
    if (red && !this.dirty) return
    this.time += red ? 0 : dt
    const { ctx, pitch, cols, ox, oy, vx, vy, mask } = this
    const t = this.time
    const R = 150
    const L = 7
    const h = Math.min(dt, 1 / 30)
    ctx.clearRect(0, 0, this.w, this.h)
    const bI: number[][] = Array.from({ length: L }, () => [])
    const bE: number[][] = Array.from({ length: L }, () => [])
    for (let q = 0; q < cellIds.length; q++) {
      const id = cellIds[q] ?? 0
      const i = id % cols
      const j = (id - i) / cols
      const cx = i * pitch + pitch / 2
      const cy = j * pitch + pitch / 2
      if (!red) {
        // Scatter away from the pointer, spring back (k 70, c 11: settles in ~0.6s).
        // Every step reads back through the Float32Arrays, as the prototype did.
        const dx = cx + (ox[q] ?? 0) - this.px
        const dy = cy + (oy[q] ?? 0) - this.py
        const dd = Math.hypot(dx, dy)
        let fx = 0
        let fy = 0
        if (dd < R && dd > 0.01) {
          const f = (1 - dd / R) ** 2 * 2800
          fx = (dx / dd) * f
          fy = (dy / dd) * f
        }
        vx[q] = (vx[q] ?? 0) + (fx - 70 * (ox[q] ?? 0) - 11 * (vx[q] ?? 0)) * h
        vy[q] = (vy[q] ?? 0) + (fy - 70 * (oy[q] ?? 0) - 11 * (vy[q] ?? 0)) * h
        ox[q] = (ox[q] ?? 0) + (vx[q] ?? 0) * h
        oy[q] = (oy[q] ?? 0) + (vy[q] ?? 0) * h
      }
      const oxq = ox[q] ?? 0
      const oyq = oy[q] ?? 0
      const v = clamp01(
        (mask[id] ?? 0) * (0.38 + 0.72 * (noise3(cx / 150, cy / 150, t * 0.85) * 0.5 + 0.5)) +
          Math.min(0.4, Math.hypot(oxq, oyq) / 40),
      )
      ;(cx < this.splitX ? bI : bE)[Math.min(L - 1, Math.floor(v * L))]?.push(cx + oxq, cy + oyq)
    }
    const max = pitch - 1
    for (let l = 0; l < L; l++) {
      const s = Math.max(1, Math.round(2 + ((max - 2) * (l + 0.5)) / L))
      for (const [b, ramp] of [
        [bI[l], ramps.grayInk],
        [bE[l], ramps.ember],
      ] as const) {
        if (!b?.length || !ramp) continue
        ctx.fillStyle = ramp[Math.min(ramp.length - 1, l)] ?? ""
        ctx.beginPath()
        for (let q = 0; q < b.length; q += 2) {
          ctx.rect(Math.round((b[q] ?? 0) - s / 2), Math.round((b[q + 1] ?? 0) - s / 2), s, s)
        }
        ctx.fill()
      }
    }
    this.dirty = false
  }
}
